// Type definitions copied from tempoissues/mock-data/issues.ts
import { Status } from "./status";
import { User } from "./users";
import { LabelInterface } from "./labels";

export interface CanvasPreview {
  id: string;
  title: string;
  identifier: string;
  description: string;
  createdAt: string;
  cycleId: string;
  rank: number;
  status: Status;
  assigned_user_id: string | null;
  labels: string[];
  project: Project | null;
  project_id: string;
  env: string;
  current_branch: string | null;
  owner_user_id: string;
}

export interface FeaturePRD {
  id?: string;
  canvas_id?: string;
  title?: string | null;
  prd?: string | null;
  status?: string | null;
  priority?: number;
  order?: number;
  due_date?: string;
  estimate?: number;
}

export interface Project {
  id: string;
  name: string;
  labels?: LabelInterface[];
  users?: User[];
}

export class Issue {
  private canvas: CanvasPreview;
  private featurePrd?: FeaturePRD;
  private projectUsers?: User[];

  constructor(
    canvas: CanvasPreview,
    featurePrd?: FeaturePRD,
    projectUsers?: User[]
  ) {
    this.canvas = canvas;
    this.featurePrd = featurePrd;
    this.projectUsers = projectUsers;
  }

  get projectId(): string {
    return this.canvas.project_id;
  }

  // Identity
  get id(): string {
    return this.canvas.id;
  }

  get identifier(): string {
    return this.canvas.identifier;
  }

  // Basic Info
  get title(): string {
    return this.featurePrd?.title || this.canvas.title || "";
  }

  set title(value: string) {
    if (this.featurePrd) {
      this.featurePrd.title = value;
    }
    this.canvas.title = value;
  }

  get description(): string {
    return this.canvas.description || this.featurePrd?.prd || "";
  }

  set description(value: string) {
    if (this.featurePrd) {
      this.featurePrd.prd = value;
    }
    this.canvas.description = value;
  }

  // Status & Priority
  get status(): Status {
    const statusId = (this.featurePrd?.status || "backlog")
      .toLowerCase()
      .replace(/\s+/g, "-");
    return {
      id: statusId,
      name: this.featurePrd?.status || "Backlog",
      color: "",
      icon: () => null,
    };
  }

  set status(value: Status) {
    if (this.featurePrd && this.featurePrd.id && this.featurePrd.canvas_id) {
      // Only update if we have the required properties
      const updatedFeaturePrd: FeaturePRD = {
        id: this.featurePrd.id,
        canvas_id: this.featurePrd.canvas_id,
        title: this.featurePrd.title,
        prd: this.featurePrd.prd,
        status: value.name,
        priority: this.featurePrd.priority,
        order: this.featurePrd.order,
      };
      this.featurePrd = updatedFeaturePrd;
    }
  }

  get priority(): number {
    return this.featurePrd?.priority || 0;
  }

  set priority(value: number) {
    if (this.featurePrd) {
      this.featurePrd.priority = value;
    }
  }

  // Assignment & Labels
  get assignee(): User | null {
    if (!this.canvas.assigned_user_id) return null;

    // Find the user in the project users array
    const user = this.projectUsers?.find(
      (u) => u.id === this.canvas.assigned_user_id
    );
    if (!user) {
      // Fallback if user not found
      return {
        id: this.canvas.assigned_user_id,
        name: "",
        email: "",
        avatarUrl: "",
      };
    }

    // Return the full user object with avatar
    return {
      id: user.id,
      name: user.name,
      email: user.email || "",
      avatarUrl: user.avatarUrl || "",
    };
  }

  set assignedUserId(value: string | null) {
    this.canvas.assigned_user_id = value;
  }

  get labels(): string[] {
    return this.canvas.labels || [];
  }

  set labels(value: string[]) {
    this.canvas.labels = value;
  }

  // Additional properties
  get estimate(): number | undefined {
    return this.featurePrd?.estimate;
  }

  get dueDate(): string | undefined {
    return this.featurePrd?.due_date;
  }

  get rank(): string {
    return String(this.featurePrd?.order ?? "");
  }

  get createdAt(): string {
    return this.canvas.createdAt || "";
  }

  get cycleId(): string {
    return this.canvas.cycleId || "";
  }

  get subissues(): any[] {
    return [];
  }

  // Raw data access (use with caution)
  getRawCanvas(): CanvasPreview {
    return { ...this.canvas };
  }

  getRawFeaturePRD(): FeaturePRD {
    return { ...this.featurePrd };
  }
}

export const ESTIMATE_POINTS = [1, 2, 3, 4, 5] as const;

export const ESTIMATE_TO_TSHIRT_SIZE: { [key: number]: string } = {
  1: "XS",
  2: "S",
  3: "M",
  4: "L",
  5: "XL",
};

export const getTShirtSize = (
  estimate: number | null | undefined
): string | undefined => {
  if (estimate === null || estimate === undefined) return undefined;
  return ESTIMATE_TO_TSHIRT_SIZE[estimate];
};
